<template>
  <q-layout view="hHh Lpr fFf">
    <!-- Spotlight background element -->
    <div class="spotlight" :style="spotlightStyle"></div>

    <q-header class="nb-header">
      <q-toolbar>
        <q-btn
          v-if="user"
          dense flat round
          icon="menu"
          class="nb-header-btn"
          @click="toggleLeftDrawer"
        />
        <q-toolbar-title>
          <div class="row items-center no-wrap">
            <div class="nb-logo-icon">
              <q-icon name="mdi-school-outline" size="18px" color="white" />
            </div>
            <span class="nb-logo-text q-mr-sm">رقمون</span>
          </div>
        </q-toolbar-title>

        <q-space />

        <q-spinner-dots v-if="isLoading" color="indigo-3" size="2em" />

        <q-btn-dropdown v-if="user" stretch flat no-caps class="nb-user-btn">
          <template v-slot:label>
            <div class="row items-center no-wrap">
              <q-avatar size="28px" class="nb-user-avatar q-ml-sm">
                <img v-if="user.photoURL" :src="user.photoURL" referrerpolicy="no-referrer" :alt="`${user.displayName} Avatar`" />
                <span v-else style="font-size: 12px; font-weight: 700; color: #A5B4FC;">{{ getUserInitial() }}</span>
              </q-avatar>
              <div class="gt-sm" style="font-size: 0.85rem; color: rgba(255,255,255,0.8); font-weight: 500;">
                {{ user.displayName || user.email }}
              </div>
            </div>
          </template>

          <q-list class="nb-dropdown-list" style="min-width: 220px;">
            <q-item class="justify-center q-pt-lg q-pb-md">
              <div class="text-center">
                <q-avatar size="60px" class="nb-user-avatar-lg q-mb-sm">
                  <img v-if="user.photoURL" :src="user.photoURL" referrerpolicy="no-referrer" />
                  <span v-else style="font-size: 22px; font-weight: 700; color: #A5B4FC;">{{ getUserInitial() }}</span>
                </q-avatar>
                <div style="font-weight: 700; color: #F1F5F9; font-size: 0.95rem;">{{ user.displayName || user.email }}</div>
              </div>
            </q-item>

            <div class="nb-dropdown-sep" />

            <q-item clickable v-close-popup :to="{ name: 'TeacherDashboard' }" class="nb-dropdown-item">
              <q-item-section avatar><q-icon name="mdi-view-dashboard-outline" size="sm" style="color: #A5B4FC;" /></q-item-section>
              <q-item-section>لوحة التحكم</q-item-section>
            </q-item>
            <q-item clickable v-close-popup :to="{ name: 'Profile' }" class="nb-dropdown-item">
              <q-item-section avatar><q-icon name="mdi-account-cog-outline" size="sm" style="color: #A5B4FC;" /></q-item-section>
              <q-item-section>الملف الشخصي</q-item-section>
            </q-item>

            <div class="nb-dropdown-sep" />

            <q-item clickable v-close-popup @click="handleLogout" class="nb-dropdown-item nb-dropdown-item--danger">
              <q-item-section avatar><q-icon name="mdi-logout" size="sm" color="negative" /></q-item-section>
              <q-item-section>تسجيل الخروج</q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>

        <div v-if="showLoginButton">
          <q-btn stretch flat :to="{ name: 'Login' }" label="تسجيل الدخول" style="color: rgba(255,255,255,0.8);" />
        </div>
      </q-toolbar>

      <EmailVerificationBanner v-if="shouldShowVerificationBanner" />
    </q-header>

    <q-drawer v-if="user" v-model="leftDrawerOpen" side="left" class="nb-drawer">
      <q-scroll-area class="fit">

        <!-- Brand -->
        <div class="nb-drawer-brand">
          <div class="nb-drawer-logo">
            <q-icon name="mdi-school-outline" size="22px" color="white" />
          </div>
          <div>
            <div class="nb-drawer-logo-text">رقمون</div>
            <div class="nb-drawer-logo-sub">منصة التعليم الذكي</div>
          </div>
        </div>

        <div class="nb-drawer-sep" />

        <q-list class="nb-menu-list">
          <q-item
            v-for="item in menuItems"
            :key="item.label"
            :to="item.to"
            clickable v-ripple exact
            active-class="nb-active-link"
            class="nb-menu-item"
          >
            <q-item-section avatar>
              <q-icon :name="item.icon" size="20px" class="nb-menu-icon" />
            </q-item-section>
            <q-item-section>
              <q-item-label class="nb-menu-label">{{ item.label }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-scroll-area>
    </q-drawer>

    <q-page-container>
      <router-view />
      <footer class="q-pa-md text-center text-caption" style="color: rgba(255,255,255,0.15); background: #0B1120;">
        رقمون {{ new Date().getFullYear() }} &copy; كل الحقوق محفوظة
      </footer>
    </q-page-container>

    <q-page-scroller position="bottom-left" :scroll-offset="150" :offset="[18, 18]" class="z-max">
      <q-btn fab icon="keyboard_arrow_up" color="indigo-5" style="z-index: 99999 !important;" />
    </q-page-scroller>
  </q-layout>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useAuthStore } from "../stores/authStore";
import { useProfileStore } from "../stores/profileStore";
import { useFeatureStore } from "../stores/featureStore"; // Import FeatureStore
import { storeToRefs } from "pinia";
import { useQuasar } from "quasar";
import { useRoute, useRouter } from "vue-router";
import EmailVerificationBanner from "./EmailVerificationBanner.vue";
import { fcmService } from "../services/fcmService";
import { deviceService } from "../services/deviceService";
import profileService from "../services/profileService";

const leftDrawerOpen = ref(localStorage.getItem("leftDrawerOpen") !== "false");

watch(leftDrawerOpen, (val) => {
  localStorage.setItem("leftDrawerOpen", val);
});

const authStore = useAuthStore();
const profileStore = useProfileStore();
const featureStore = useFeatureStore(); // Init FeatureStore
const { user, isLoading, emailVerified } = storeToRefs(authStore);
const { professionalProfile } = storeToRefs(profileStore);
const { logout } = authStore;

// Trigger feature and profile fetch on mount (layout loads once)
onMounted(async () => {
  if (user.value) {
    fcmService.initializeNotifications();
    fcmService.listenForMessages();
    
    if (!featureStore.isLoaded) {
      try { await featureStore.fetchFeatures(); } catch(e) {}
    }
    
    // Fetch profile if user is logged in but profile is missing
    if (!profileStore.hasProfileData) {
      try { await profileStore.fetchProfile(); } catch(e) {}
    }
  }
});

const $q = useQuasar();
const route = useRoute();
const router = useRouter();

// Spotlight tracking
const mouseX = ref(50);
const mouseY = ref(50);
const spotlightStyle = computed(() => ({
  '--x': `${mouseX.value}%`,
  '--y': `${mouseY.value}%`
}));

const handleMouseMove = (e) => {
  mouseX.value = (e.clientX / window.innerWidth) * 100;
  mouseY.value = (e.clientY / window.innerHeight) * 100;
};

onMounted(() => {
  window.addEventListener('mousemove', handleMouseMove);
});

const showLoginButton = computed(() => {
  const authPages = ["Login", "Register", "ForgotPassword"];
  return !user.value && !isLoading.value && !authPages.includes(route.name);
});

watch(
  () => $q.dark.isActive,
  (isDark) => {
    // Force light mode classes
    document.body.classList.add('light');
    document.body.classList.add('body--light');
    document.body.classList.remove('body--dark');
  },
  { immediate: true }
);

onMounted(() => {
  // Always force Light Mode on startup
  $q.dark.set(false);
  localStorage.setItem("darkMode", "false");
  document.body.classList.add('light');
  document.body.classList.add('body--light');
  document.body.classList.remove('body--dark');
});

function toggleLeftDrawer() {
  leftDrawerOpen.value = !leftDrawerOpen.value;
}

const getUserInitial = () => {
  const name = user.value?.displayName || user.value?.email;
  return name ? name.charAt(0).toUpperCase() : "?";
};

const handleLogout = async () => {
  try {
    await logout();
    router.push({ name: "Login" });
  } catch (error) {
    console.error("Logout Error:", error);
    $q.notify({ type: "negative", message: "فشل تسجيل الخروج." });
  }
};

const menuItems = computed(() => {
  if (!user.value) return [];

  const items = [
    {
      label: "لوحة التحكم",
      icon: "mdi-view-dashboard-outline",
      to: { name: "TeacherDashboard" },
    },
    {
      label: "الملف الشخصي",
      icon: "mdi-account-cog-outline",
      to: { name: "Profile" },
    },
    {
      label: "إدارة الطلاب",
      icon: "mdi-account-group-outline",
      to: { name: "ManageStudents" },
    },
    {
      label: "شحن الرصيد",
      icon: "mdi-credit-card-plus-outline",
      to: { name: "RechargeCredits" },
    },
    {
      label: "الكراس اليومي",
      icon: "mdi-notebook-plus-outline",
      to: { name: "CreateNotebook" },
    },
    {
      label: "سجل الكراسات",
      icon: "mdi-notebook-outline",
      to: { name: "MyNotebooks" },
    },
    {
      label: "قوالب المذكرات",
      icon: "mdi-table-edit",
      to: { name: "TemplateManager" },
    },
    {
      label: "دليل الاستخدام",
      icon: "mdi-help-circle-outline",
      to: { name: "HelpGuide" },
    }
  ];

  // Add Admin Dashboard and AI Test Generator ONLY for admin
  if (profileStore.professionalProfile?.role === 'admin') {
    // Check if AI Test Generator is in the list, if not add it (or manage via feature flags)
    items.push({
      label: "مولد الاختبارات",
      icon: "mdi-file-certificate-outline",
      to: { name: "AITestGenerator" },
    });

    items.push({
      label: "لوحة الإدارة",
      icon: "mdi-shield-account-outline",
      to: { name: "AdminDashboard" },
    });

    items.push({
      label: "إدارة المناهج",
      icon: "mdi-bookshelf",
      to: { name: "CurriculumManager" },
    });
  }

  // Final filter based on Feature Flags for non-admins
  return items.filter(item => {
    if (profileStore.professionalProfile?.role === 'admin') return true;
    if (!item.to || !item.to.name) return true;
    return featureStore.getFeatureStatus(item.to.name);
  });
});

const shouldShowVerificationBanner = computed(() => {
  return (
    user.value &&
    !emailVerified.value &&
    user.value.providerData?.[0]?.providerId === "password"
  );
});

watch(
  user,
  async (newUser) => {
    if (newUser) {
      console.log("[Layout.vue] User object updated.");
      fcmService.initializeNotifications();
      fcmService.listenForMessages();

      // 🔥 بصمة الجهاز لمنع تكرار الحسابات (منقولة من onMounted لضمان الدقة)
      try {
        const deviceId = await deviceService.getDeviceId();
        if (deviceId) await profileService.updateDeviceId(deviceId);
      } catch (e) { console.error("Device ID tracking error:", e); }

      // Ensure profile is fetched on login to show Admin links
      if (!profileStore.hasProfileData) {
        try { await profileStore.fetchProfile(); } catch(e) {}
      }
    }
    else {
      console.log("[Layout.vue] User logged out.");
      profileStore.clearProfile();
    }
  },
  { deep: true },
);

watch(shouldShowVerificationBanner, (newValue) => {
  console.log(
    `[Layout.vue] 'shouldShowVerificationBanner' is now: ${newValue}`,
  );
});
</script>

<style lang="scss" scoped>
/* ── Header ── */
:deep(.nb-header) {
  background: rgba(11, 17, 32, 0.97) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  box-shadow: 0 1px 20px rgba(0, 0, 0, 0.4);
}

.nb-header-btn {
  color: rgba(255, 255, 255, 0.6);
  &:hover { color: #fff; }
}

/* ── Logo ── */
.nb-logo-icon {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: linear-gradient(135deg, #6366F1, #4F46E5);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.4);
}
.nb-logo-text {
  font-size: 1rem;
  font-weight: 800;
  background: linear-gradient(90deg, #A5B4FC, #6366F1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ── User button ── */
.nb-user-btn {
  color: rgba(255, 255, 255, 0.75) !important;
  &:hover { color: #fff !important; }
}
.nb-user-avatar {
  background: rgba(99, 102, 241, 0.2);
  border: 1px solid rgba(99, 102, 241, 0.3);
}
.nb-user-avatar-lg {
  background: rgba(99, 102, 241, 0.2);
  border: 2px solid rgba(99, 102, 241, 0.4);
}

/* ── Dropdown (placeholder — real styles in global block below) ── */

/* ── Drawer ── */
:deep(.nb-drawer) {
  background: #0C1422 !important;
  border-right: none !important;
  border-left: 1px solid rgba(255, 255, 255, 0.06) !important;
}

/* Drawer brand section */
.nb-drawer-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 16px 16px;
}
.nb-drawer-logo {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: linear-gradient(135deg, #6366F1, #4F46E5);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 14px rgba(99, 102, 241, 0.35);
  flex-shrink: 0;
}
.nb-drawer-logo-text {
  font-size: 1.05rem;
  font-weight: 800;
  background: linear-gradient(90deg, #A5B4FC, #6366F1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
}
.nb-drawer-logo-sub {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.3);
  margin-top: 2px;
}
.nb-drawer-sep {
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 0 12px 8px;
}

/* ── Menu items ── */
.nb-menu-list {
  padding: 0 8px;
}
.nb-menu-item {
  border-radius: 10px;
  margin-bottom: 2px;
  color: rgba(255, 255, 255, 0.5);
  transition: background 0.2s, color 0.2s;
  min-height: 44px;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.85);

    .nb-menu-icon { color: #A5B4FC !important; }
  }
}
.nb-menu-icon {
  color: rgba(255, 255, 255, 0.3) !important;
  transition: color 0.2s;
}
.nb-menu-label {
  font-size: 0.88rem;
  font-weight: 500;
}

/* Active menu link */
.nb-active-link {
  background: rgba(99, 102, 241, 0.14) !important;
  color: #A5B4FC !important;
  border-right: 3px solid #6366F1;

  .nb-menu-icon { color: #A5B4FC !important; }
  .nb-menu-label { font-weight: 600; }
}
</style>

<!-- Global (unscoped) styles for teleported dropdown -->
<style lang="scss">
.nb-dropdown-list {
  background: #131B2E !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 14px !important;
  overflow: hidden;
  padding: 4px 0;
}

.nb-dropdown-sep {
  height: 1px;
  background: rgba(255, 255, 255, 0.07);
  margin: 4px 0;
}

.nb-dropdown-item {
  color: rgba(255, 255, 255, 0.7) !important;
  font-size: 0.88rem;
  border-radius: 8px;
  margin: 2px 6px;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: rgba(99, 102, 241, 0.12) !important;
    color: #fff !important;
  }

  &--danger {
    color: rgba(248, 113, 113, 0.8) !important;
    &:hover {
      background: rgba(239, 68, 68, 0.1) !important;
      color: #F87171 !important;
    }
  }
}
</style>
